import React, { useState, useEffect } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Scale, RefreshCw, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin-Panel: lex.ivoliq Early-Access-Warteliste (Juli 2026).
 * Zeigt Anwalts-Leads (GET /api/lex/waitlist, Admin-only) + Wochentrend.
 */

function weekKey(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return `KW ${String(week).padStart(2, '0')}`;
}

function buildWeeklyTrend(items, weeks = 8) {
  const now = new Date();
  const buckets = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i * 7);
    buckets.push({ key: weekKey(d), count: 0 });
  }
  const map = Object.fromEntries(buckets.map((b) => [b.key, b]));
  items.forEach((it) => {
    const d = new Date(it.created_at);
    if (isNaN(d) || (now - d) / 86400000 > weeks * 7) return;
    const k = weekKey(d);
    if (map[k]) map[k].count += 1;
  });
  return buckets;
}

export default function LexWaitlistPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invitingEmail, setInvitingEmail] = useState(null);
  const [golive, setGolive] = useState(null);
  const [directName, setDirectName] = useState('');
  const [directEmail, setDirectEmail] = useState('');
  const [sendingDirect, setSendingDirect] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  const toggleManual = async (it) => {
    const done = it.status !== 'ok';
    setTogglingKey(it.key);
    try {
      await api.post(`/lex/golive/manual/${it.key}`, { done });
      toast.success(done ? 'Punkt abgehakt.' : 'Haken entfernt.');
      await loadGolive();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen.');
    } finally {
      setTogglingKey(null);
    }
  };

  const directInvite = async (e) => {
    e.preventDefault();
    if (!directName.trim() || !directEmail.trim()) return;
    if (!window.confirm(`Persönliche Einladung an ${directEmail.trim()} senden?`)) return;
    setSendingDirect(true);
    try {
      await api.post('/lex/invites', { email: directEmail.trim(), name: directName.trim() });
      toast.success(`Einladung an ${directEmail.trim()} gesendet.`);
      setDirectName(''); setDirectEmail('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Einladung fehlgeschlagen.');
    } finally {
      setSendingDirect(false);
    }
  };

  const loadGolive = () => api.get('/lex/golive').then((r) => setGolive(r.data)).catch(() => {});
  useEffect(() => { loadGolive(); }, []);

  const invite = async (it) => {
    if (!window.confirm(`Persönliche Einladung an ${it.email} senden?`)) return;
    setInvitingEmail(it.email);
    try {
      await api.post('/lex/invites', { email: it.email, name: it.name });
      toast.success(`Einladung an ${it.email} gesendet.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Einladung fehlgeschlagen.');
    } finally {
      setInvitingEmail(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/lex/waitlist');
      setData(res.data);
    } catch (e) {
      console.error('lex-Warteliste laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="py-8 text-center text-gray-500" data-testid="lex-waitlist-loading">Lade lex-Warteliste…</div>;
  }
  if (!data) return null;

  const items = data.items || [];
  const now = Date.now();
  const last7 = items.filter((i) => now - new Date(i.created_at).getTime() < 7 * 86400000).length;
  const trend = buildWeeklyTrend(items);

  return (
    <div className="space-y-6" data-testid="lex-waitlist-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="h-6 w-6" style={{ color: '#C5A059' }} />
            lex.ivoliq — Early-Access-Warteliste
          </h2>
          <p className="text-gray-600 mt-1">Anwalts-Leads von der lex-Landing (Prod: Landing-only-Modus).</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800" data-testid="lex-waitlist-refresh">
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </div>

      {golive && (
        <div className="bg-white border rounded-xl p-5" style={{ borderColor: golive.go ? '#059669' : '#C5A059' }} data-testid="lex-golive-checklist">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-sm font-bold text-gray-900">
              🚦 Go/No-Go-Checkliste (Pilot-Launch)
              <span className={`ml-2 px-2 py-0.5 text-xs font-semibold rounded ${golive.go ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="lex-golive-status">
                {golive.go ? `Technisch GO (${golive.auto_ok}/${golive.auto_total})` : `${golive.auto_ok}/${golive.auto_total} technisch grün`}
              </span>
              <span className="ml-2 text-xs text-gray-400">{golive.manual_open} manuelle Punkte offen</span>
            </h3>
            <button onClick={loadGolive} className="text-xs text-gray-400 underline" data-testid="lex-golive-refresh">neu prüfen</button>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
            {golive.items.map((it) => (
              <li key={it.key} className="text-xs flex items-start gap-2" data-testid={`lex-golive-${it.key}`}>
                {it.manual ? (
                  <input type="checkbox" checked={it.status === 'ok'} onChange={() => toggleManual(it)}
                    disabled={togglingKey === it.key}
                    className="mt-0.5 h-3.5 w-3.5 accent-emerald-600 cursor-pointer"
                    data-testid={`lex-golive-check-${it.key}`} />
                ) : (
                  <span>{it.status === 'ok' ? '✅' : '❌'}</span>
                )}
                <span className={it.status === 'fail' ? 'text-rose-700 font-semibold' : 'text-gray-700'}>
                  {it.label}{it.detail ? <span className="text-gray-400"> — {it.detail}</span> : null}
                  {it.manual && it.status === 'ok' && it.done_at && (
                    <span className="text-emerald-600"> ✓ {new Date(it.done_at).toLocaleDateString('de-DE')}</span>
                  )}
                  {it.manual && it.status !== 'ok' && <span className="text-gray-400"> (manuell abhaken)</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Card data-testid="lex-direct-invite-card">
        <CardContent className="pt-5">
          <p className="text-sm font-semibold text-gray-700 mb-1">Direkt einladen</p>
          <p className="text-xs text-gray-500 mb-3">Sendet die persönliche Einladungs-Mail (14 Tage gültig) — auch ohne Wartelisten-Eintrag.</p>
          <form onSubmit={directInvite} className="flex flex-wrap items-end gap-2">
            <input value={directName} onChange={(e) => setDirectName(e.target.value)} required placeholder="Name"
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-44" data-testid="lex-direct-invite-name" />
            <input type="email" value={directEmail} onChange={(e) => setDirectEmail(e.target.value)} required placeholder="anwalt@kanzlei.at"
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-60" data-testid="lex-direct-invite-email" />
            <button type="submit" disabled={sendingDirect}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 border transition-colors hover:bg-gray-50 disabled:opacity-50"
              style={{ color: '#0A1128', borderColor: '#C5A059' }} data-testid="lex-direct-invite-btn">
              <Send className="h-3 w-3" /> {sendingDirect ? 'Sende…' : 'Einladung senden'}
            </button>
          </form>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="pt-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Einträge gesamt</p>
          <p className="text-3xl font-bold mt-1" style={{ color: '#0A1128' }} data-testid="lex-waitlist-total">{data.count}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Letzte 7 Tage</p>
          <p className="text-3xl font-bold mt-1" style={{ color: '#0A1128' }} data-testid="lex-waitlist-last7">{last7}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5">
          <p className="text-xs uppercase tracking-wide text-gray-500">Neuester Eintrag</p>
          <p className="text-sm font-semibold mt-2 text-gray-800" data-testid="lex-waitlist-latest">
            {items[0] ? `${items[0].name} · ${new Date(items[0].created_at).toLocaleDateString('de-DE')}` : '—'}
          </p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="pt-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">Neue Einträge pro Woche (8 Wochen)</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="key" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                <Tooltip formatter={(v) => [v, 'Einträge']} />
                <Bar dataKey="count" fill="#C5A059" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-5 overflow-x-auto">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center" data-testid="lex-waitlist-empty">Noch keine Einträge.</p>
          ) : (
            <table className="w-full text-sm" data-testid="lex-waitlist-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">E-Mail</th>
                  <th className="py-2 pr-3">Kanzlei</th>
                  <th className="py-2 pr-3 text-right">Eingetragen</th>
                  <th className="py-2 text-right">Pilot-Einladung</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.email} className="border-b border-gray-100">
                    <td className="py-2 pr-3 font-medium text-gray-900">{it.name}</td>
                    <td className="py-2 pr-3"><a href={`mailto:${it.email}`} className="text-emerald-700 hover:underline">{it.email}</a></td>
                    <td className="py-2 pr-3 text-gray-600">{it.kanzlei_name || '—'}</td>
                    <td className="py-2 pr-3 text-right text-gray-500 text-xs">{new Date(it.created_at).toLocaleString('de-DE')}</td>
                    <td className="py-2 text-right">
                      {it.invited_at ? (
                        <span className="text-xs font-medium text-emerald-700" data-testid={`lex-invited-${it.email}`}>
                          ✓ Eingeladen {new Date(it.invited_at).toLocaleDateString('de-DE')}
                        </span>
                      ) : (
                        <button onClick={() => invite(it)} disabled={invitingEmail === it.email}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 border transition-colors hover:bg-gray-50"
                          style={{ color: '#0A1128', borderColor: '#C5A059' }}
                          data-testid={`lex-invite-btn-${it.email}`}>
                          <Send className="h-3 w-3" /> {invitingEmail === it.email ? 'Sende…' : 'Einladen'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
